/**
 * CAPTCHA vidjeti (ЧТЗ 4.2.11.3 — murojaat formasida reCAPTCHA majburiy).
 *
 * Kalit backendda yoqilgan bo'lsa (`RECAPTCHA_ENABLED=1`) — Google reCAPTCHA v2
 * yoki Yandex SmartCaptcha skripti faqat forma ko'ringanda yuklanadi. Kalit
 * sozlanmagan konturda vidjet chiqmaydi, backend tekshiruvni o'tkazib yuboradi.
 */
import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";

type Provider = "recaptcha" | "smartcaptcha";

interface CaptchaApi {
  render: (element: HTMLElement, options: Record<string, unknown>) => number;
  reset: (id?: number) => void;
}

declare global {
  interface Window {
    grecaptcha?: CaptchaApi & { ready?: (callback: () => void) => void };
    smartCaptcha?: CaptchaApi;
    __sfCaptchaReady?: () => void;
  }
}

const SCRIPTS: Record<Provider, string> = {
  recaptcha: "https://www.google.com/recaptcha/api.js?onload=__sfCaptchaReady&render=explicit",
  smartcaptcha: "https://smartcaptcha.yandexcloud.net/captcha.js?render=onload&onload=__sfCaptchaReady",
};

let loading: Promise<void> | null = null;

function loadScript(provider: Provider, lang: string): Promise<void> {
  if (loading) return loading;
  loading = new Promise<void>((resolve, reject) => {
    window.__sfCaptchaReady = () => resolve();
    const script = document.createElement("script");
    script.src = `${SCRIPTS[provider]}&hl=${encodeURIComponent(lang)}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      loading = null;
      reject(new Error("captcha_load_failed"));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export interface CaptchaState {
  /** Vidjet kerakmi (backendda yoqilgan va kalit berilgan). */
  enabled: boolean;
  token: string;
  error: string;
  reset: () => void;
}

export function useCaptcha(
  container: RefObject<HTMLDivElement>,
  config: { enabled: boolean; provider: Provider; site_key: string } | undefined,
  lang: string,
): CaptchaState {
  const enabled = Boolean(config?.enabled && config.site_key);
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const widget = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !config || !container.current) return;
    let cancelled = false;
    loadScript(config.provider, lang)
      .then(() => {
        const api = config.provider === "smartcaptcha" ? window.smartCaptcha : window.grecaptcha;
        if (cancelled || !api || !container.current || widget.current !== null) return;
        widget.current = api.render(container.current, {
          sitekey: config.site_key,
          hl: lang,
          callback: (value: string) => setToken(value),
          "expired-callback": () => setToken(""),
        });
      })
      .catch(() => {
        if (!cancelled) setError("CAPTCHA xizmatini yuklab bo'lmadi — internet aloqasini tekshiring.");
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, config, container, lang]);

  const reset = () => {
    setToken("");
    const api = config?.provider === "smartcaptcha" ? window.smartCaptcha : window.grecaptcha;
    if (api && widget.current !== null) api.reset(widget.current);
  };

  return { enabled, token, error, reset };
}
